import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@src/components/Button/Button';
import { ChevronLeftIcon } from '@src/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@src/components/icons/ChevronRightIcon';
import { classes } from '@src/components/layout/space';
import { testIdAttr, testIdFor } from '@src/components/testId';
import './Carousel.css';

export interface CarouselProps {
  children: ReactNode;
  prevLabel: string;
  nextLabel: string;
  testId?: string;
}

/**
 * Horizontal snap track with prev/next arrows. Arrows hide when the row
 * already fits — same behaviour as the v1 home carousels.
 */
export function Carousel({ children, prevLabel, nextLabel, testId }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const maxScroll = track.scrollWidth - track.clientWidth;
    setScrollable(maxScroll > 1);
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(maxScroll <= 1 || track.scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    syncEdges();
    track.addEventListener('scroll', syncEdges, { passive: true });
    window.addEventListener('resize', syncEdges);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncEdges);
    observer?.observe(track);
    return () => {
      track.removeEventListener('scroll', syncEdges);
      window.removeEventListener('resize', syncEdges);
      observer?.disconnect();
    };
  }, [syncEdges]);

  function slide(direction: number) {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const trackLeft = track.getBoundingClientRect().left;
    const offsets = Array.from(track.children).map(
      (card) => card.getBoundingClientRect().left - trackLeft + track.scrollLeft,
    );
    if (!offsets.length) {
      return;
    }
    let current = 0;
    offsets.forEach((_, index) => {
      if (Math.abs(offsets[index] - track.scrollLeft) < Math.abs(offsets[current] - track.scrollLeft)) {
        current = index;
      }
    });
    const next = Math.min(offsets.length - 1, Math.max(0, current + direction));
    track.scrollTo({ left: offsets[next], behavior: 'smooth' });
  }

  return (
    <div
      className={classes('carousel', scrollable && 'carousel--scrollable')}
      {...testIdAttr(testId)}
    >
      {scrollable && (
        <Button
          variant="ghost"
          size="large"
          icon={<ChevronLeftIcon />}
          aria-label={prevLabel}
          disabled={atStart}
          testId={testIdFor(testId, 'prev')}
          className="carousel__nav carousel__nav--prev"
          onClick={() => slide(-1)}
        />
      )}
      <div className="carousel__track" ref={trackRef} {...testIdAttr(testIdFor(testId, 'track'))}>
        {children}
      </div>
      {scrollable && (
        <Button
          variant="ghost"
          size="large"
          icon={<ChevronRightIcon />}
          aria-label={nextLabel}
          disabled={atEnd}
          testId={testIdFor(testId, 'next')}
          className="carousel__nav carousel__nav--next"
          onClick={() => slide(1)}
        />
      )}
    </div>
  );
}
